import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { jobsApi } from "../services/jobs.api";
import type { FailedJob, ScheduledTask, ScheduledTaskFilterState } from "../types";

/** Navbat holati sekundlarda eskiradi — polling intervali. */
const QUEUE_POLL_MS = 10_000;

/**
 * Navbat sanoqlari.
 *
 * ⚠️ POLLING bilan yangilanadi (10 s) — bu OPERATSION sahifa va eskirgan
 * raqam noto'g'ri qaror keltirardi («xato yo'q» deb yopib ketish).
 */
export function useQueueCounts() {
	return useQuery({
		queryKey: ["jobs", "queues"],
		queryFn: () => jobsApi.queues(),
		refetchInterval: QUEUE_POLL_MS,
	});
}

/**
 * BullMQ Job Scheduler'lari.
 *
 * ⚠️ Polling YO'Q: schedulerlar `settings.timezone` o'zgarganda yoki
 * worker qayta ishga tushganda o'zgaradi, ya'ni daqiqalarda emas,
 * kunlarda. Har 10 soniyada barcha navbatlarni `getJobSchedulers()` bilan
 * so'rash Redis'ga keraksiz yuk bo'lardi.
 *
 * ⚠️ Hook `JobsPage` ning YUQORI darajasida chaqiriladi (tab ichida emas),
 * ya'ni jadval tabi ochilmasa ham bitta so'rov ketadi — bu ATAYLAB:
 * polling yo'q va `staleTime` 60 s, ya'ni sahifa umri davomida amalda
 * BITTA so'rov. `useFailedJobs` da mount/unmount hiylasi kerak bo'lgan
 * sabab (har navbat uchun ALOHIDA so'rov) bu yerda yo'q.
 */
export function useSchedulers() {
	return useQuery({
		queryKey: ["jobs", "schedulers"],
		queryFn: () => jobsApi.schedulers(),
		staleTime: 60_000,
	});
}

/**
 * Navbatning yiqilgan joblari.
 *
 * ⚠️ Hook faqat navbat TANLANGANDA mount qilinadi (`FailedJobsPanel`
 * drawer ichida) — `usePaginatedQuery` da `enabled` yo'q, ya'ni shartni
 * mount/unmount bilan ifodalash yagona to'g'ri yo'l. Aks holda sahifa
 * ochilishida har navbat uchun bitta ortiqcha so'rov ketardi.
 */
export function useFailedJobs(queueName: string) {
	return usePaginatedQuery<FailedJob>({
		queryKey: ["jobs", "failed", queueName],
		url: `/jobs/queues/${queueName}/failed`,
		params: {},
	});
}

export function useScheduledTasks(filters: ScheduledTaskFilterState) {
	const params: Record<string, string> = {};

	if (filters.kind) {
		params.kind = filters.kind;
	}
	if (filters.status) {
		params.status = filters.status;
	}

	return usePaginatedQuery<ScheduledTask>({
		queryKey: ["jobs", "scheduled-tasks"],
		url: "/jobs/scheduled-tasks",
		params,
	});
}

/**
 * ⚠️ Muvaffaqiyatdan keyin BUTUN `["jobs"]` daraxti invalidatsiya
 * qilinadi: retry navbat sanoqlarini ham, xato joblar ro'yxatini ham
 * o'zgartiradi, va faqat bittasini yangilash sahifada qarama-qarshi
 * raqamlar qoldirardi.
 *
 * ⚠️ Xato xabari `getErrorMessage` orqali XOM ko'rsatiladi: backend
 * ataylab aniq javob beradi — `processing` vazifa uchun 422 «Rejali
 * vazifa hozir bajarilmoqda», ro'yxatdan chiqib ketgan job uchun 404
 * «Vazifa topilmadi». Umumiy xabar bu farqni yo'qotardi.
 */
function useJobsMutation<TVariables>(
	mutationFn: (variables: TVariables) => Promise<unknown>,
	successMessage: string
) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["jobs"] });
			message.success(successMessage);
		},
		onError: (error) => {
			message.error(getErrorMessage(error, "Vazifani qayta navbatga qo'yib bo'lmadi"));
		},
	});
}

export function useRetryJob() {
	return useJobsMutation(
		({ queue, id }: { queue: string; id: string }) => jobsApi.retryJob(queue, id),
		"Job qayta navbatga qo'yildi"
	);
}

export function useRetryScheduledTask() {
	return useJobsMutation(
		(id: string) => jobsApi.retryScheduledTask(id),
		"Vazifa qayta navbatga qo'yildi"
	);
}
